import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { takeDestination, toPath } from '../utils/returnTo'
import Brand from '../components/layout/Brand'
import GoogleSignInButton from '../components/auth/GoogleSignInButton'

export default function SignIn() {
  const { user, loading, signingIn, error, signIn } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Already signed in — go straight to the intended destination, or home,
  // instead of showing the sign-in screen.
  useEffect(() => {
    if (loading || !user) return

    /**
     * Two sources for one answer, read in order of freshness.
     *
     * The router's own state is right whenever the sign-in happened without the page
     * reloading, which is the ordinary popup flow. The stored copy covers the flows where
     * it did reload and the state went with it — a link opened out of Messenger into a
     * real browser, a refresh on this screen, a phone reclaiming the backgrounded tab.
     *
     * `takeDestination` runs either way, not only when the router state is missing: it
     * clears as it reads, and a note left behind would fire again on the next sign-in in
     * this tab.
     */
    const stored = takeDestination()
    const dest = toPath(location.state?.from) ?? stored ?? '/home'

    navigate(dest, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user])

  if (loading || user) {
    return <p className="text-ink-3 text-body-s">Loading…</p>
  }

  return (
    // The mark sits at the top; the claim and the action sit at the bottom,
    // where a thumb rests. mt-auto on the heading opens the gap between them.
    <div className="w-full max-w-[400px] mx-auto flex-1 flex flex-col">
      <Brand />

      <h1 className="mt-auto font-display font-bold text-display-l text-ink">
        Go Anywhere.
        <br />
        Get Anything.
      </h1>

      <p className="mt-3 text-body text-ink-2">
        Pick a delivery service in Laguna, say what you need, and someone rides for it.
      </p>

      <div className="mt-7">
        <GoogleSignInButton onClick={signIn} loading={signingIn} />

        {error && (
          <p role="alert" className="text-alert text-body-s mt-2.5">
            {error}
          </p>
        )}

        <p className="mt-3.5 text-[12px] leading-[18px] text-ink-3">
          By continuing you agree to the Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  )
}
